import { useEffect, useMemo, useState } from 'react'
import { useNavigate, useSearch } from '@tanstack/react-router'
import {
  EmptyState,
  MasterDetail,
  useFullscreen,
  type MasterDetailItem,
} from '@/components/ui'
import { useVendorPricelists } from '../api/queries'
import type { VendorRow } from '../api/types'
import { VendorPricelistDetail } from '../components/VendorPricelistDetail'

/**
 * Rebuilt 2026-05-28 to mirror Customer Pricelists exactly — MasterDetail
 * layout (vendor list left, expanded detail card right), same primitives,
 * same chrome. The supplierinfo data layer + sample exclusion at
 * `/api/vendor-pricelists/*` is the only structural difference from the
 * Customer Pricelists module.
 *
 * `?vendor=N` deep-link: select the matching row by id; if missing,
 * surface an inline banner and drop the param.
 */

interface ListRow extends MasterDetailItem {
  vendor: VendorRow
}

export function VendorPricelistsPage() {
  const [query, setQuery] = useState('')
  const [debounced, setDebounced] = useState('')
  const [selectedId, setSelectedId] = useState<string | undefined>(undefined)
  const { fullscreen, toggle: toggleFullscreen } = useFullscreen()

  const navigate = useNavigate()
  const search = useSearch({ strict: false }) as { vendor?: string | number }
  const vendorParam = search?.vendor
  const vendorId = useMemo(() => {
    if (vendorParam === undefined || vendorParam === '') return null
    const n = Number(vendorParam)
    return Number.isFinite(n) && n > 0 ? n : null
  }, [vendorParam])

  useEffect(() => {
    const id = setTimeout(() => setDebounced(query), 200)
    return () => clearTimeout(id)
  }, [query])

  const { data, isLoading, isError } = useVendorPricelists(debounced)
  const vendors = useMemo<VendorRow[]>(() => data?.vendors ?? [], [data])

  // Deep-link error: invalid `?vendor=` value, or value points at a
  // vendor not in the current list (filtered out, doesn't exist, etc).
  const deepLinkError = useMemo<string | null>(() => {
    if (vendorParam !== undefined && vendorId === null) {
      return `Invalid ?vendor value: ${String(vendorParam)}`
    }
    if (vendorId === null) return null
    if (isLoading) return null
    if (!vendors.some((v) => v.id === vendorId)) {
      return `Vendor ${vendorId} not found or has no supplierinfo rows.`
    }
    return null
  }, [vendorParam, vendorId, isLoading, vendors])

  // On successful match: select the row, then drop the param.
  const [resolvedForVendorId, setResolvedForVendorId] = useState<number | null>(null)
  useEffect(() => {
    if (vendorId === null) return
    if (resolvedForVendorId === vendorId) return
    if (isLoading) return
    if (vendors.some((v) => v.id === vendorId)) {
      // Legitimate "setState in response to async external state
      // transitions" — the vendors list arriving from the server is an
      // event from outside React. Same pattern as the Customer
      // Pricelists deep-link path. Rule suppressed per this specific
      // call.
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setSelectedId(String(vendorId))
    }
    setResolvedForVendorId(vendorId)
  }, [vendorId, isLoading, vendors, resolvedForVendorId])

  // Drop the URL param once we've acted on it.
  useEffect(() => {
    if (vendorId === null && vendorParam === undefined) return
    if (resolvedForVendorId !== vendorId) return
    navigate({
      to: '.',
      search: (prev: Record<string, unknown> | undefined) => {
        if (!prev || !('vendor' in prev)) return prev ?? {}
        const next = { ...prev }
        delete next.vendor
        return next
      },
      replace: true,
    })
  }, [vendorId, vendorParam, resolvedForVendorId, navigate])

  const items = useMemo<ListRow[]>(() => {
    return vendors.map((v) => ({
      id: String(v.id),
      primary: v.name,
      secondary: v.country_name ?? undefined,
      meta: (
        <>
          <span>{v.item_count.toLocaleString()} items</span>
          {v.city ? (
            <>
              <span className="opacity-40">·</span>
              <span>{v.city}</span>
            </>
          ) : null}
        </>
      ),
      vendor: v,
    }))
  }, [vendors])

  return (
    <div
      className={
        fullscreen
          ? 'fixed inset-0 z-50 flex flex-col bg-canvas'
          : 'flex h-full flex-col'
      }
    >
      <header className="flex flex-col gap-1 border-b border-border-subtle bg-surface px-6 py-3">
        <h1 className="text-role-page-title leading-tight tracking-tight">
          Vendor Pricelists
        </h1>
        <p className="text-sm text-secondary">
          Per-vendor cost, lead time, and incoterm coverage from product.supplierinfo. Sample products excluded.
        </p>
      </header>

      {deepLinkError ? (
        <div className="border-b border-danger bg-danger-bg px-6 py-2 text-sm text-danger-text">
          {deepLinkError}{' '}
          <button
            type="button"
            onClick={() => {
              navigate({
                to: '.',
                search: (prev: Record<string, unknown> | undefined) => {
                  if (!prev) return {}
                  const next = { ...prev }
                  delete next.vendor
                  return next
                },
                replace: true,
              })
              setResolvedForVendorId(null)
            }}
            className="ml-2 cursor-pointer text-xs underline"
          >
            Dismiss
          </button>
        </div>
      ) : null}

      <div className="min-h-0 flex-1 overflow-hidden p-4">
        {isError ? (
          <EmptyState
            variant="error"
            title="Couldn't load vendors"
            description="The /api/vendor-pricelists endpoint returned an error. Check that the middleware container is running the current build."
          />
        ) : (
          <MasterDetail<ListRow>
            items={items}
            selectedId={selectedId}
            onSelect={setSelectedId}
            onSearch={setQuery}
            searchPlaceholder={
              isLoading
                ? 'Loading vendors…'
                : `Search ${vendors.length} vendor pricelists`
            }
            autoSelectFirst
            renderDetail={(row) => (
              <VendorPricelistDetail
                vendor={row.vendor}
                fullscreen={fullscreen}
                onToggleFullscreen={toggleFullscreen}
              />
            )}
          />
        )}
      </div>
    </div>
  )
}
